import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import { getArticleBySlug, latestArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-4">文章不存在</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 underline cursor-pointer">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const relatedArticles = latestArticles
    .filter((a) => a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.excerpt,
          image: article.image,
          datePublished: article.date,
          author: { '@type': 'Person', name: article.author },
          publisher: {
            '@type': 'Organization',
            name: '91爆料网',
            logo: { '@type': 'ImageObject', url: 'http://gm3pta.cn/logo.png' },
          },
          mainEntityOfPage: { '@type': 'WebPage', '@id': `http://gm3pta.cn/article/${article.slug}` },
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'http://gm3pta.cn/' },
            { '@type': 'ListItem', position: 2, name: article.categoryName, item: `http://gm3pta.cn/category/${article.category}` },
            { '@type': 'ListItem', position: 3, name: article.title, item: `http://gm3pta.cn/article/${article.slug}` },
          ],
        }}
      />

      <Navbar />

      <main className="pt-16">
        <article className="py-10 md:py-14 bg-background-50">
          <div className="px-4 md:px-6 max-w-4xl mx-auto">
            <Breadcrumb categorySlug={article.category} articleSlug={article.slug} />

            <div className="flex flex-wrap items-center gap-2 mb-4">
              <Link
                to={`/category/${article.category}`}
                className="px-3 py-1 bg-primary-100 text-primary-700 text-xs rounded-full hover:bg-primary-200 transition-colors cursor-pointer"
              >
                {article.categoryName}
              </Link>
              {article.tags.map((tag) => (
                <span key={tag} className="px-2.5 py-1 bg-background-100 text-foreground-500 text-xs rounded-full">
                  #{tag}
                </span>
              ))}
            </div>

            <h1 className="font-heading text-2xl md:text-4xl font-black text-foreground-950 mb-6 leading-tight">
              {article.title}
            </h1>

            <div className="flex items-center gap-4 text-foreground-400 text-sm mb-8 pb-6 border-b border-background-200/70">
              <span><i className="ri-user-line mr-1"></i>{article.author}</span>
              <span><i className="ri-calendar-line mr-1"></i>{article.date}</span>
              <span><i className="ri-eye-line mr-1"></i>{(article.views / 10000).toFixed(1)}万阅读</span>
              <span><i className="ri-chat-3-line mr-1"></i>{article.comments}评论</span>
            </div>

            <div className="rounded-lg overflow-hidden mb-8">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-[300px] md:h-[400px] object-cover"
              />
            </div>

            <div
              className="prose prose-lg max-w-none text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex flex-col sm:flex-row items-center justify-between mt-10 pt-6 border-t border-background-200/70">
              <div className="flex items-center gap-4 mb-4 sm:mb-0">
                <span className="text-foreground-500 text-sm">分享到：</span>
                {['ri-wechat-line', 'ri-weibo-line', 'ri-link'].map((icon) => (
                  <span
                    key={icon}
                    className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-100 hover:text-primary-500 transition-colors cursor-pointer"
                  >
                    <i className={icon}></i>
                  </span>
                ))}
              </div>
              <Link
                to={`/category/${article.category}`}
                className="text-primary-500 hover:text-primary-600 text-sm font-medium transition-colors cursor-pointer"
              >
                查看更多{article.categoryName} <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-14 bg-background-100">
            <div className="px-4 md:px-6 max-w-7xl mx-auto">
              <div className="flex items-center gap-4 mb-8">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                  相关<span className="text-primary-500">爆料</span>
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.slug}`}
                    className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-300 transition-all duration-300 cursor-pointer"
                  >
                    <div className="h-[180px] overflow-hidden">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-4">
                      <span className="text-xs text-primary-500 mb-1 block">{ra.categoryName}</span>
                      <h3 className="font-heading text-sm font-bold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
                        {ra.title}
                      </h3>
                      <p className="text-foreground-400 text-xs mt-2">{ra.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}